import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [25, 100, 500];
const FREQUENCIES = [
  { value: 'once', label: 'One-time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

export default function Donate() {
  const [searchParams] = useSearchParams();
  const [frequency, setFrequency] = useState('once');
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get('canceled') === '1') {
      toast.error('Donation canceled. You can try again whenever you\'re ready.');
    }
  }, [searchParams]);

  const finalAmount = isCustom ? parseFloat(customAmount) : amount;
  const isRecurringCustomBlocked = frequency !== 'once' && isCustom;

  const handlePresetClick = (v) => {
    setAmount(v);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error('Please enter a valid donation amount.');
      return;
    }
    if (isRecurringCustomBlocked) {
      toast.error('Recurring donations are available only for preset amounts. Pick $25, $100 or $500 — or switch to One-time.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/donations/checkout`, {
        amount: parseFloat(finalAmount),
        frequency,
        donor_name: name || null,
        donor_email: email || null,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned.');
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Could not start checkout.';
      toast.error(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="donate-page">
      <section className="bg-black text-white py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-accent font-manrope font-bold tracking-widest uppercase mb-4">Support the Mission</p>
            <h1 className="font-unbounded font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Fuel the next<br/>generation of<br/>AI builders.
            </h1>
            <p className="font-manrope text-gray-300 text-lg max-w-2xl">
              Every dollar funds residencies, mentorship, compute and the spaces where young
              founders live, learn, and ship with AI. Donate once or commit recurring giving — both go directly to programming.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <form
          onSubmit={handleSubmit}
          className="bg-white border-2 border-black p-8 sm:p-12 shadow-[8px_8px_0_0_rgba(0,0,0,1)]"
          data-testid="donate-form"
        >
          <div className="mb-10">
            <Label className="text-xs uppercase font-manrope font-bold tracking-widest text-black mb-4 block">
              Frequency
            </Label>
            <div className="grid grid-cols-3 gap-2 border-2 border-black" data-testid="frequency-tabs">
              {FREQUENCIES.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrequency(f.value)}
                  data-testid={`frequency-${f.value}`}
                  className={`py-4 font-manrope font-bold uppercase tracking-wide transition-all ${
                    frequency === f.value
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <Label className="text-xs uppercase font-manrope font-bold tracking-widest text-black mb-4 block">
              Amount (USD)
            </Label>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`preset-${v}`}
                  onClick={() => handlePresetClick(v)}
                  className={`py-5 border-2 border-black font-unbounded font-black text-2xl transition-all ${
                    !isCustom && amount === v
                      ? 'bg-accent text-white border-accent shadow-electric'
                      : 'bg-white text-black hover:bg-black hover:text-white'
                  }`}
                >
                  ${v}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-unbounded font-black text-xl">$</span>
              <Input
                type="number"
                min="1"
                step="0.01"
                placeholder="Custom amount"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setIsCustom(true);
                }}
                onFocus={() => setIsCustom(true)}
                className="pl-10 h-14 border-2 border-black font-manrope text-lg rounded-none"
                data-testid="custom-amount-input"
              />
            </div>
            {isRecurringCustomBlocked && (
              <p className="mt-3 flex items-center gap-2 text-sm font-manrope text-red-600" data-testid="recurring-warning">
                <AlertCircle size={16} />
                Recurring giving is available for preset amounts. Pick $25, $100 or $500 — or switch to One-time.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <div>
              <Label htmlFor="donor-name" className="text-xs uppercase font-manrope font-bold tracking-widest text-black mb-2 block">
                Name (optional)
              </Label>
              <Input
                id="donor-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-12 border-2 border-black rounded-none font-manrope"
                data-testid="donor-name-input"
              />
            </div>
            <div>
              <Label htmlFor="donor-email" className="text-xs uppercase font-manrope font-bold tracking-widest text-black mb-2 block">
                Email (optional)
              </Label>
              <Input
                id="donor-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 border-2 border-black rounded-none font-manrope"
                data-testid="donor-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || isRecurringCustomBlocked}
            data-testid="donate-submit-button"
            className="w-full h-16 bg-black text-white font-unbounded font-black text-xl uppercase tracking-wide rounded-none border-2 border-black hover:bg-accent hover:border-accent hover:shadow-electric transition-all disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="animate-spin mr-2" size={20}/> Redirecting…</>
            ) : (
              <><Heart size={20} className="mr-2"/> Donate {finalAmount ? `$${Number(finalAmount).toFixed(2)}` : ''}{frequency !== 'once' ? ` / ${frequency === 'monthly' ? 'month' : 'year'}` : ''}</>
            )}
          </Button>

          <div className="mt-6 flex items-center gap-2 text-sm font-manrope text-gray-500">
            <ShieldCheck size={16} />
            <span>Secure checkout powered by Stripe. AI House USA never stores your card details.</span>
          </div>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16" data-testid="impact-grid">
          {[
            { amount: '$25', impact: 'Sponsors one builder workshop kit.' },
            { amount: '$100', impact: 'Provides a week of compute credits for residents.' },
            { amount: '$500', impact: 'Funds a full mentorship cycle for a cohort.' },
          ].map((item) => (
            <div key={item.amount} className="border-l-4 border-accent pl-4">
              <p className="font-unbounded font-black text-3xl mb-1">{item.amount}</p>
              <p className="font-manrope text-gray-700 text-sm">{item.impact}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
